import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MessageSquare, FileText, ExternalLink, Send, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function SupportPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    store: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // In production, this would send to a backend endpoint
    setSubmitted(true);
    toast.success('Mensaje enviado correctamente');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="support-page">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Button>
          <div className="flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-zinc-900">Soporte y Ayuda</h1>
          </div>
          <p className="text-zinc-600 mt-2">Estamos aquí para ayudarte con Spin 360° Fusion2V</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        
        {/* Quick Help Cards */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Guía Rápida
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-zinc-600 mb-3">
                Aprende a configurar tu primer producto con vista 360°.
              </p>
              <Button variant="outline" size="sm" className="w-full">
                Ver guía
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-primary" />
                Sirv Help
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-zinc-600 mb-3">
                Documentación oficial de Sirv para spins 360°.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => window.open('https://sirv.com/help/', '_blank')}
              >
                Ir a Sirv
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Email Directo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-zinc-600 mb-3">
                Escríbenos directamente por email.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => window.location.href = 'mailto:info@fusionpublicidad.com.ar'}
              >
                Enviar email
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Section */}
        <Card>
          <CardHeader>
            <CardTitle>Preguntas Frecuentes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-b border-zinc-100 pb-4">
              <h4 className="font-semibold text-zinc-900 mb-2">¿Cómo configuro mi primer producto con vista 360°?</h4>
              <p className="text-sm text-zinc-600">
                1. Ve al Dashboard y selecciona un producto. 2. Ingresa la URL de tu spin de Sirv. 
                3. Guarda la configuración. El widget aparecerá automáticamente en tu tienda.
              </p>
            </div>
            <div className="border-b border-zinc-100 pb-4">
              <h4 className="font-semibold text-zinc-900 mb-2">¿Necesito una cuenta de Sirv?</h4>
              <p className="text-sm text-zinc-600">
                Sí, necesitas una cuenta en sirv.com para subir tus imágenes y crear los spins 360°. 
                Puedes configurar tus credenciales de Sirv en la página de Configuración.
              </p>
            </div>
            <div className="border-b border-zinc-100 pb-4">
              <h4 className="font-semibold text-zinc-900 mb-2">¿Cómo agrego hotspots a mis productos?</h4>
              <p className="text-sm text-zinc-600">
                Los hotspots se configuran directamente en sirv.com. Abre tu archivo .spin en Sirv, 
                usa el editor de hotspots para agregar puntos interactivos, y automáticamente 
                aparecerán en tu tienda.
              </p>
            </div>
            <div className="pb-2">
              <h4 className="font-semibold text-zinc-900 mb-2">¿El widget afecta la velocidad de mi tienda?</h4>
              <p className="text-sm text-zinc-600">
                No. El widget se carga de forma asíncrona y Sirv optimiza automáticamente las 
                imágenes para una carga rápida. El impacto en la velocidad es mínimo.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Envíanos un Mensaje
            </CardTitle>
            <CardDescription>
              ¿No encontraste la respuesta? Escríbenos y te responderemos a la brevedad.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-zinc-900 mb-2">¡Mensaje Enviado!</h3>
                <p className="text-zinc-600 mb-4">
                  Gracias por contactarnos. Te responderemos dentro de las próximas 24-48 horas.
                </p>
                <Button onClick={() => setSubmitted(false)} variant="outline">
                  Enviar otro mensaje
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Tu nombre"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@email.com"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="store">URL de tu tienda (opcional)</Label>
                  <Input
                    id="store"
                    name="store"
                    value={formData.store}
                    onChange={handleChange}
                    placeholder="tutienda.mitiendanube.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Asunto</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="¿En qué podemos ayudarte?"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Mensaje</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Describe tu consulta o problema en detalle..."
                    rows={5}
                    required
                  />
                </div>
                <Button type="submit" className="w-full">
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Mensaje
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card>
          <CardHeader>
            <CardTitle>Información de Contacto</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-zinc-900 mb-2">Email</h4>
                <a href="mailto:info@fusionpublicidad.com.ar" className="text-primary hover:underline">
                  info@fusionpublicidad.com.ar
                </a>
              </div>
              <div>
                <h4 className="font-semibold text-zinc-900 mb-2">Web</h4>
                <a href="https://www.fusionpublicidad.com.ar" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  www.fusionpublicidad.com.ar
                </a>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-zinc-100">
              <p className="text-sm text-zinc-600">
                <strong>Horario de atención:</strong> Lunes a Viernes, 9:00 - 18:00 (Argentina)
              </p>
              <p className="text-sm text-zinc-600 mt-1">
                <strong>Tiempo de respuesta:</strong> 24-48 horas hábiles
              </p>
            </div>
          </CardContent>
        </Card>

      </main>

      <footer className="bg-white border-t border-zinc-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center text-zinc-600">
          <p>© 2026 Spin 360° Fusion2V. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
